import {
    useAppNavigate,
    useCharacterAppearanceCatalog,
    useCharacterUpdates,
    useCurrentPlayerCharacter,
    useDeveloperAccess,
    useEditorAccess,
    useIsScreenCovered,
} from '@sorrem-client/ui/hooks';
import { createValueStore } from '@sorrem-client/ui/shared/value-store';
import { useCallback, useState } from 'react';
import { GuidedTour } from '@sorrem-client/ui/features/GuidedTour';
import styles from './CharacterHome.module.scss';
import { CharacterHomeBackdrop, CharacterHomeHeader, CharacterHomePanels, CommissionOfferProvider } from './components';
import {
    useCharacterCreationDraft,
    useCharacterHomeRoute,
    useCharacterHomeTours,
    useCharacterHomeTabHotkeys,
    useSkillsTabScene,
} from './hooks';
import { CHARACTER_HOME_TAB_LIST, type CharacterHomeTab } from './tabs';

interface CharacterHomeProps {
    /** Ends the session; resolves once it is cleared. */
    onLogout: () => Promise<void>;
}

export function CharacterHome({ onLogout }: CharacterHomeProps): JSX.Element {
    const navigate = useAppNavigate();
    const {
        accountProfile,
        characters,
        character: player,
        isLoading: isPlayerLoading,
        error: playerError,
    } = useCurrentPlayerCharacter();
    // Pushes the selected character whenever its stored state changes (a
    // crucible payout, a spend), so this page never shows stale essence.
    useCharacterUpdates();
    const { activeTab, selectedId, inviteCode, isExplicitCreateRoute } = useCharacterHomeRoute();

    // Character creation renders in place of the summary panel/tabs/docked
    // bar (see CharacterHomePanels) rather than as its own standalone
    // route+page - either because the player explicitly navigated to
    // /character/create (e.g. "Create new character" in Manage Characters),
    // or because the loaded account simply has no characters yet.
    const isCreationViewShown = isExplicitCreateRoute
        || (accountProfile !== null && !isPlayerLoading && characters.length === 0);

    const { isDeveloper, debugPages } = useDeveloperAccess();
    const { isCreator, editorPages } = useEditorAccess();
    const { bodyParts, equipmentPieces } = useCharacterAppearanceCatalog();
    const draft = useCharacterCreationDraft(bodyParts, isCreationViewShown);

    // The Journey gives the whole screen below the header to its books, lying open on the writing table.
    const isJourneyTab = !isCreationViewShown && activeTab === 'journey';
    const isSkillsTab = !isCreationViewShown && activeTab === 'skills';
    const skillsScene = useSkillsTabScene(isSkillsTab);
    // The selected skill or dial (`dial:<branch>`); its details show in the right panel.
    const [selectedSkillTreeId, setSelectedSkillTreeId] = useState<string | null>(null);

    // The tab under the pointer; its prop in the room highlights. A store, so a hover re-renders only the room.
    const [hoveredTabStore] = useState(() => createValueStore<CharacterHomeTab | null>(null));
    // Until the home and the sky are drawn, a loading message covers everything.
    const [isHomeLoading, setIsHomeLoading] = useState(true);
    // A cutscene or vision playing over the home sends every panel (and what
    // floats over them, like callouts) away until it ends.
    const isScreenCovered = useIsScreenCovered();
    const isHomeShown = !isHomeLoading && !isScreenCovered;
    // Guided tours (#234): the page's first, then each tab's the first time it opens.
    const tours = useCharacterHomeTours(activeTab, isHomeShown && !isCreationViewShown);

    const handleTabChange = useCallback((tabId: CharacterHomeTab): void => {
        // The page input ignores keys while a button has focus, so let go of
        // the clicked tab and its hotkeys keep working.
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        navigate(`/character/${tabId}`);
    }, [navigate]);
    useCharacterHomeTabHotkeys(activeTab, !isCreationViewShown, handleTabChange);

    const handleLogoutClick = useCallback((): void => {
        void onLogout().then(() => navigate('/login'));
    }, [navigate, onLogout]);

    return (
        // Commission offers play from here, once, whatever tab asks (#330).
        <CommissionOfferProvider isOverviewShown={isHomeShown && !isCreationViewShown && activeTab === 'overview'}>
            <div className={styles.screen}>
                <CharacterHomeBackdrop
                    player={player}
                    isCreationViewShown={isCreationViewShown}
                    draft={draft}
                    isSkillsTab={isSkillsTab}
                    isJourneyTab={isJourneyTab}
                    hoveredTabStore={hoveredTabStore}
                    skillsScene={skillsScene}
                    selectedSkillTreeId={selectedSkillTreeId}
                    onSelectSkillTreeId={setSelectedSkillTreeId}
                    onLoadingChange={setIsHomeLoading}
                />

                {/* The shell lets the pointer through to the room (and the Skills tab's tree) except on the header and visible panels. */}
                <div className={styles.shell}>
                    <CharacterHomeHeader
                        accountProfile={accountProfile}
                        isDeveloper={isDeveloper}
                        debugPages={debugPages}
                        isCreator={isCreator}
                        editorPages={editorPages}
                        onNavigate={navigate}
                        onLogout={handleLogoutClick}
                        tabs={CHARACTER_HOME_TAB_LIST}
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        onTabHover={hoveredTabStore.set}
                        areTabsShown={isHomeShown && !isCreationViewShown}
                        className={styles.header}
                    />

                    <div ref={skillsScene.mainRegionRef} className={styles.mainRegion} aria-hidden="true" />

                    <CharacterHomePanels
                        player={player}
                        isPlayerLoading={isPlayerLoading}
                        playerError={playerError}
                        isCreationViewShown={isCreationViewShown}
                        activeTab={activeTab}
                        selectedCrucibleId={selectedId}
                        inviteCode={inviteCode}
                        draft={draft}
                        bodyParts={bodyParts}
                        equipmentPieces={equipmentPieces}
                        skillTreeViewStore={skillsScene.viewStore}
                        selectedSkillTreeId={selectedSkillTreeId}
                        isHomeShown={isHomeShown}
                    />
                </div>

                <GuidedTour tourId='character-home' isEnabled={tours.isPageTourEnabled} />
                {/* Keyed by tour, so moving to another tab starts that tab's tour afresh. */}
                {tours.tabTourId !== null && <GuidedTour key={tours.tabTourId} tourId={tours.tabTourId} isEnabled />}
            </div>
        </CommissionOfferProvider>
    );
}
